import { useTranslation } from "react-i18next"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import { getAvatarColorByRole, getRoleName } from "@/modules/common/helpers/common-helper"
import { UserRole } from "@/types/__generated__/union-types"

type TooltipCustomProps = React.ComponentPropsWithRef<typeof TooltipPrimitive.Root>

type HealthTooltipType = {
  name?: string
  data?: string
  timestamp?: string
  stamp?: string
  additional?: string
  image?: string
}
type UserTooltipType = {
  shortName: string | undefined
  firstName: string | null | undefined
  lastName: string | null | undefined
  role: UserRole | undefined
  professionalTitle: string | undefined
}
export const TooltipProvider = TooltipPrimitive.Provider
export const Tooltip = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger
export const TooltipPortal = TooltipPrimitive.Portal
export const TooltipContent = (props: React.ComponentPropsWithRef<typeof TooltipPrimitive.Content>) => {
  const { className, sideOffset = 4, ...rest } = props
  return (
    <TooltipPrimitive.Content
      sideOffset={sideOffset}
      data-testid="tool-tip-content"
      className={cn(
        "tooltip bg-surface-inverted text-on-surface-inverted shadow-3 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-100 rounded-md px-2 py-1 text-sm leading-4 font-normal not-italic",
        className,
      )}
      {...rest}
    />
  )
}

export const UserTooltip = ({ shortName, role, firstName, lastName, professionalTitle }: UserTooltipType) => {
  const { t } = useTranslation()

  return (
    <div className="user-tooltip flex items-center gap-2 py-1">
      <Avatar className="wrapper-avatar-tooltip h-10 w-10 cursor-pointer items-center justify-center transition ease-in-out">
        <AvatarFallback className={`flex text-sm font-medium ${getAvatarColorByRole(role)}`}>
          {shortName}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col justify-center">
        <div className="username-user-tooltip text-sm leading-5">
          {[professionalTitle, firstName, lastName].filter(Boolean).join(" ")}
        </div>
        {role && (
          <div className="title-user-tooltip flex items-center text-xs leading-4 tracking-[0.25px]">
            <span>{t(getRoleName(role))}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export const HealthTooltip = ({ timestamp, additional, data, image, stamp, name }: HealthTooltipType) => {
  return (
    <div className="health-tooltip">
      <div className="data-health-tooltip text-sm leading-4">{data}</div>
      <div className="add-health-tooltip text-xs leading-4 tracking-[0.25px]">{additional}</div>
      <div className="time-health-tooltip flex h-8 items-center gap-2 py-1">
        <Avatar className="wrapper-avatar-tooltip h-6 w-6 cursor-pointer items-center justify-center transition ease-in-out">
          <AvatarImage src={image} alt={name} className="avatar-tooltip" />
          <AvatarFallback className="bg-lilac-surface text-on-lilac-surface flex text-sm font-medium">
            {name}
          </AvatarFallback>
        </Avatar>
        <div className="stamp-health-tooltip text-xs leading-4 tracking-[0.25px]">
          <span>{stamp ? stamp : "Last updated on"} </span>
          <span>{timestamp}</span>
        </div>
      </div>
    </div>
  )
}

export const TooltipCustom = ({ children, ...otherProps }: TooltipCustomProps) => {
  return (
    <TooltipProvider>
      <Tooltip {...otherProps} delayDuration={300}>
        {children}
      </Tooltip>
    </TooltipProvider>
  )
}
